import type { FlagKey } from "./types";
import { TOUR_STEPS, type TourMoment, type TourStep } from "./tour-copy.ts";

/**
 * The flags that have tutorial steps (shared/tour-copy.ts TOUR_STEPS with a `feature`).
 * A flag without steps, such as originalAssets, must never start a feature tour: there would
 * be nothing to show. tests/tours.test.ts checks this list against the steps.
 */
export const FLAGS_WITH_TOUR_STEPS: readonly FlagKey[] = ["focusUI", "bountyUI", "quickUI", "economyUI", "notesUI", "upgradesUI", "achievementsUI"];

/**
 * Features that are on, have tour steps, and whose steps this device has not been shown yet.
 * Order follows `enabled`, which callers keep in FLAG_KEYS order. The tour shows only one of them
 * per app open (oneFeatureTour); the rest wait for later opens, one at a time.
 */
export function pendingFeatureTours(enabled: readonly FlagKey[], toured: readonly FlagKey[]): FlagKey[] {
  return enabled.filter((key) => FLAGS_WITH_TOUR_STEPS.includes(key) && !toured.includes(key));
}

/**
 * The one feature a feature tour shows this open: the first pending feature with something to show
 * on this board (`showable`; the gold steps need an open task), so a feature waiting for a task does
 * not hold up the others. Empty when none can show. Only that feature is marked toured.
 */
export function oneFeatureTour(pending: readonly FlagKey[], showable: (key: FlagKey) => boolean): FlagKey[] {
  const key = pending.find(showable);
  return key ? [key] : [];
}

/**
 * Whether a tour may open now. Only one tour per app open: once the first run or a feature tour has
 * shown, a feature tour asked for later in the same open waits for the next one. The first run
 * itself (`firstRun`) always may: it is the newcomer's only tour that open, and the question mark
 * in Profile replays it on request.
 */
export function tourMayOpen(firstRun: boolean, touredThisOpen: boolean): boolean {
  return firstRun || !touredThisOpen;
}

// ---------- moments (2026-10-04) ----------
//
// The tour plays in segments, each the first time its moment happens on this device (owner:
// "Rather than play the full tutorial up front, play the relevant segments of it as things occur
// naturally"). A card is remembered by its title once shown (`seen`), so a feature switched on
// later still gets its card at the next occurrence, and the whole-tour replay from Profile marks
// nothing.

/** A card's key in the device's seen list. */
export function stepKey(step: Pick<TourStep, "title">): string {
  return step.title;
}

/**
 * The cards a moment plays now: its steps, in tour order, whose feature (if any) is on and that
 * this device has not seen. Empty when there is nothing new to say.
 */
export function stepsForMoment(moment: TourMoment, enabled: readonly FlagKey[], seen: readonly string[]): TourStep[] {
  return TOUR_STEPS.filter((step) => step.moment === moment && (step.feature === undefined || enabled.includes(step.feature)) && !seen.includes(stepKey(step)));
}

/**
 * Whether a moment's cards may show now. Never over another tour. A "board" moment (Next up, the
 * note button showing for the first time) waits for an open in which no tour has shown yet, so the
 * first open is the welcome alone and a newcomer is not buried; the moments a person brings about
 * (opening a task, finishing one, opening Profile, Rewards or Upgrades) play when they happen.
 */
export function momentMayOpen(moment: TourMoment, showing: boolean, touredThisOpen: boolean): boolean {
  if (showing) return false;
  return moment !== "board" || !touredThisOpen;
}

/**
 * What a device that had the old tours has already seen, for the first run under moments: the
 * first-run cards when the old tour was done, and every card of each feature it was toured on.
 */
export function seenFromLegacy(tutorialDone: boolean, touredFeatures: readonly FlagKey[]): string[] {
  return TOUR_STEPS.filter((step) => (step.feature === undefined ? tutorialDone : touredFeatures.includes(step.feature))).map(stepKey);
}
